import imageCompression from 'browser-image-compression';
import { supabase } from '@/lib/supabase';

const BUCKET = 'content-images';
/** Matches the Dropbox migration: ample for a ~360px slot on a retina screen. */
const MAX_EDGE = 900;

export interface UploadedContentImage {
  /** Public URL of the resized copy -- the one to store on a record. */
  url: string;
  originalBytes: number;
  smallerBytes: number;
}

function slugify(name: string): string {
  return (
    name
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\.[^.]+$/, '')
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'afbeelding'
  );
}

/**
 * Puts one image in the content-images bucket and hands back the URL to store.
 *
 * Two objects are written, the same pair the Dropbox migration produced: the
 * original as an archive, and a <stem>_smaller.webp capped at MAX_EDGE which is
 * what the app requests. Resizing happens here, in the browser, so a 5 MB photo
 * off a phone is never uploaded at full size.
 *
 * Writing to this bucket requires the admin role; the storage policy enforces
 * that, so a non-admin gets a rejected upload rather than a silent success.
 */
export async function uploadContentImage(
  file: File,
): Promise<UploadedContentImage> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Kies een afbeelding (jpg, png of webp).');
  }

  const stem = slugify(file.name);
  // Short suffix so two people uploading "achtergrond.jpg" don't collide.
  const unique = Math.random().toString(16).slice(2, 10);
  const ext = (file.name.match(/\.([a-zA-Z0-9]+)$/)?.[1] || 'jpg').toLowerCase();

  const smaller = await imageCompression(file, {
    maxWidthOrHeight: MAX_EDGE,
    fileType: 'image/webp',
    initialQuality: 0.82,
    useWebWorker: true,
  });

  const { error: originalError } = await supabase.storage
    .from(BUCKET)
    .upload(`${stem}-${unique}.${ext}`, file, { contentType: file.type });
  if (originalError) throw originalError;

  const smallerPath = `${stem}-${unique}_smaller.webp`;
  const { error: smallerError } = await supabase.storage
    .from(BUCKET)
    .upload(smallerPath, smaller, { contentType: 'image/webp' });
  if (smallerError) throw smallerError;

  const {
    data: { publicUrl },
  } = supabase.storage.from(BUCKET).getPublicUrl(smallerPath);

  return {
    url: publicUrl,
    originalBytes: file.size,
    smallerBytes: smaller.size,
  };
}
